import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Button,
  Avatar,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import CommentBox from "../../components/CommentBox/CommentBox";
import { formatDistanceToNow } from "date-fns";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import API_BASE_URL from "../../utils/api";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import Divider from "@mui/material/Divider";

const ThreadDetail = () => {
  const { id } = useParams();
  const [thread, setThread] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/threads/${id}`)
      .then((res) => {
        if (res.data) {
          setThread(res.data);
        } else {
          setError("This thread no longer exists or has been deleted.");
        }
      })
      .catch(() =>
        setError("This thread no longer exists or has been deleted.")
      );
  }, [id]);

  if (error) {
    return (
      <Box
        sx={{
          width: "100%",
          textAlign: "center",
          marginTop: "50px",
        }}
      >
        <Typography variant="h5" color="error" gutterBottom>
          {error}
        </Typography>
        <Button variant="outlined" onClick={() => navigate("/Chat-Thread")}>
          Back to Threads
        </Button>
      </Box>
    );
  }

  if (!thread) return <div>Loading...</div>;

  const postedBy = thread.isGuestThread
    ? thread.guestName || "Guest User"
    : thread.author?.name || "Unknown User";

  const profileImage = thread.isGuestThread ? "" : thread.author?.profileImage;

  return (
    <Paper
      elevation={0}
      sx={{
        width: "auto",
        padding: { xs: "18px", sm: "26px", md: "28px 70px" },
        margin: { xs: "12px auto", md: "20px auto" },
        borderRadius: "16px",
        backgroundColor: "#ffffff",
        border: "1px solid #eee",
      }}
    >
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        sx={{ marginBottom: 2 }}
        onClick={() => navigate(`/Chat-Thread`)}
      >
        View All Threads
      </Button>

      {/* Author Info */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          mb: 2,
          pb: 2,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Avatar
          src={profileImage}
          alt={postedBy}
          sx={{ width: 42, height: 42 }}
        >
          {postedBy?.charAt(0)}
        </Avatar>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {postedBy}
            </Typography>

            {thread.isGuestThread && (
              <Box
                sx={{
                  backgroundColor: "#fff3cd",
                  color: "#856404",
                  px: 1,
                  py: 0.2,
                  borderRadius: "999px",
                  fontSize: "0.7rem",
                  fontWeight: 700,
                }}
              >
                Guest
              </Box>
            )}
          </Box>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {formatDistanceToNow(new Date(thread.createdAt), {
              addSuffix: true,
            })}
          </Typography>
        </Box>
      </Box>

      {/* Title & Content */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 1.5,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
          {thread.title}
        </Typography>

        {thread.visibility === "workshop" && (
          <Box
            sx={{
              backgroundColor: "#f2f2f2",
              color: "#444",
              px: 1.2,
              py: 0.45,
              borderRadius: "999px",
              fontSize: "0.78rem",
              fontWeight: 800,
              border: "1px solid #e6e6e6",
              whiteSpace: "nowrap",
            }}
          >
            Workshop Only
          </Box>
        )}
      </Box>
      <Box
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: "1px solid #eee",
          backgroundColor: "#fff",
          mb: 3,
        }}
      >
        <Box
          sx={{
            color: "#2b2b2b",
            fontSize: "1rem",
            "& *": { lineHeight: 1.85 },
            "& p": { marginBottom: "12px" },
            "& img": { maxWidth: "100%", borderRadius: "12px" },
            "& a": { color: "#1a73e8", fontWeight: 600 },
            "& blockquote": {
              borderLeft: "4px solid #FFD700",
              pl: 2,
              color: "#444",
              background: "#fffdf0",
              py: 1,
              borderRadius: 1,
            },
          }}
          dangerouslySetInnerHTML={{ __html: thread.content }}
        />
      </Box>

      {/* Attachments */}

      {thread.attachments?.map((file, index) => (
        <Paper
          key={index}
          elevation={0}
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            border: "1px solid #eee",
            backgroundColor: "#fafafa",
          }}
        >
          {file.type === "image" ? (
            <Box
              component="img"
              src={file.url}
              alt="attachment"
              sx={{
                width: "100%",
                maxHeight: 340,
                objectFit: "cover",
                borderRadius: 2,
              }}
            />
          ) : (
            <Button href={file.url} target="_blank" variant="outlined">
              View PDF
            </Button>
          )}
        </Paper>
      ))}

      <Box
        sx={{
          mt: 4,
          borderRadius: 3,
          border: "1px solid #eee",
          overflow: "hidden",
          backgroundColor: "#fafafa",
        }}
      >
        {/* Header strip */}
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#f6f6f6",
            borderBottom: "1px solid #eee",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ChatBubbleOutlineIcon sx={{ color: "#666" }} />
            <Typography sx={{ fontWeight: 900, fontSize: "1.05rem" }}>
              Comments
            </Typography>

            {/* optional: show count if you pass it from backend later */}
            {/* <Box sx={{
        ml: 1,
        px: 1.1,
        py: 0.2,
        borderRadius: "999px",
        fontSize: "0.75rem",
        fontWeight: 800,
        backgroundColor: "#fff",
        border: "1px solid #e5e5e5",
        color: "#444",
      }}>
        {thread.commentsCount ?? 0}
      </Box> */}
          </Box>

          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Replies are collapsed — expand to read more
          </Typography>
        </Box>

        {/* Content area */}
        <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
          <CommentBox
            threadId={thread._id}
            threadAuthorId={thread.author?._id || null}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default ThreadDetail;
